"use client";

import { useSyncExternalStore } from "react";
import { setShallowSearchParams, useShallowSearchParam } from "@/lib/hooks/use-shallow-search-param";
import { resolveAreaSport } from "./area-g3-format";

/**
 * The area page's selected toggle sport, shared by every island on the page (the hero toggle,
 * the scope sync, the other-sports cards, the map layers). The URL is the single source of truth:
 * `?sport=<key>` (a shallow History API write, so the ISR HTML never re-renders and the canonical
 * never changes), or a `#sport=<key>` fragment, which the conditions layout
 * (ConditionsFragmentLens) turns into `?sport=` after hydration anyway. Server HTML and the first
 * client render see the primary sport.
 */

export const AREA_SPORT_PARAM = "sport";

/** The id of the hero's sport toggle, the scroll target after a pick from further down the page. */
export const AREA_SPORT_TOGGLE_ID = "area-sport-toggle";

function subscribeHash(callback: () => void): () => void {
  window.addEventListener("hashchange", callback);
  return () => window.removeEventListener("hashchange", callback);
}

/** The selected sport (one of `allowed`), else the primary. */
export function useAreaSport<K extends string>(primary: K, allowed: readonly K[]): K {
  const param = useShallowSearchParam(AREA_SPORT_PARAM);
  const hash = useSyncExternalStore(subscribeHash, () => window.location.hash, () => "");
  return resolveAreaSport({ param, hash }, allowed, primary) as K;
}

/**
 * Show a sport on this page: the primary sport clears the param, any other writes it. With
 * `scroll`, the page scrolls back to the hero toggle so the reader sees the switched panels, and
 * focus moves to that sport's toggle chip.
 */
export function selectAreaSport(sport: string, options: Readonly<{ primary: string; scroll?: boolean }>): void {
  // A `#sport=` fragment that has not been applied yet would win over the param; drop it.
  if (/(?:^#|&)sport=/u.test(window.location.hash)) {
    const url = new URL(window.location.href);
    url.hash = url.hash.replace(/(^#|&)sport=[^&]*/u, "$1").replace(/^#&/u, "#").replace(/&$/u, "");
    if (url.hash === "#") url.hash = "";
    window.history.replaceState(window.history.state, "", url);
  }
  setShallowSearchParams({ [AREA_SPORT_PARAM]: sport === options.primary ? null : sport }, { history: "replace" });
  if (options.scroll) {
    const target = document.getElementById(AREA_SPORT_TOGGLE_ID);
    if (target) {
      const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
      const top = target.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top: Math.max(0, top), behavior: reduce ? "auto" : "smooth" });
      // The picked card hides once its sport shows (it would drop focus to the body): move focus
      // to the toggle chip now pressed, so keyboard and screen reader users land with the switch.
      target.querySelector?.<HTMLElement>(`[data-area-chip="${sport.replace(/[^a-z0-9-]/giu, "")}"]`)?.focus({ preventScroll: true });
    }
  }
}
